import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LoginResponse, PermissionKey, SessionUser } from '@portal/shared';
import { api, refreshSession, registerSessionListener, setAccessToken } from './api';

const IDLE_TIMEOUT_MS = 30 * 60_000;

interface AuthState {
  user: SessionUser | null;
  ready: boolean;
  login: (email: string, password: string, totp?: string) => Promise<SessionUser>;
  setSession: (s: LoginResponse) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [ready, setReady] = useState(false);
  const qc = useQueryClient();

  const setSession = useCallback((s: LoginResponse | null) => {
    setAccessToken(s?.accessToken ?? null);
    setUser(s?.user ?? null);
  }, []);

  useEffect(() => {
    registerSessionListener(setSession);
    refreshSession().then((s) => {
      setSession(s);
      setReady(true);
    });
  }, [setSession]);

  const login = useCallback(
    async (email: string, password: string, totp?: string) => {
      const s = await api.post<LoginResponse>('/auth/login', { email, password, totp });
      setSession(s);
      return s.user;
    },
    [setSession],
  );

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => undefined);
    setSession(null);
    qc.clear();
  }, [qc, setSession]);

  const refreshUser = useCallback(async () => {
    const me = await api.get<SessionUser>('/auth/me');
    setUser(me);
  }, []);

  // Session timeout: sign out after 30 minutes without user activity.
  const lastActivity = useRef(Date.now());
  useEffect(() => {
    if (!user) return;
    const bump = () => (lastActivity.current = Date.now());
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const timer = setInterval(() => {
      if (Date.now() - lastActivity.current > IDLE_TIMEOUT_MS) void logout();
    }, 30_000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(timer);
    };
  }, [user, logout]);

  const value = useMemo(
    () => ({ user, ready, login, setSession, logout, refreshUser }),
    [user, ready, login, setSession, logout, refreshUser],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** The signed-in user (only use inside authenticated routes). */
export function useUser(): SessionUser {
  const { user } = useAuth();
  if (!user) throw new Error('Not signed in');
  return user;
}

/** Hides actions the user lacks. The API enforces the same rules. */
export function usePermission() {
  const { user } = useAuth();
  return useCallback(
    (...anyOf: PermissionKey[]) => !!user && anyOf.some((p) => user.permissions.includes(p)),
    [user],
  );
}
